// MacroMod primitive — the "material statistics vary spatially" modulator (Option D, first slice).
//
// WHY THIS EXISTS (the wide-zoom flatness argument):
// The correct anti-aliased colour of a fragment is E[c | pixel footprint].  For a plain fbm the
// fine octaves are a STATIONARY random field — their statistics do not depend on position — so
// E[c] is a CONSTANT and zooming out correctly converges to one flat colour.  There is genuinely
// no structure at that scale to recover; no amount of contrast restoration inside fbmVal can
// invent it (that is the hard ceiling on the "analytic variance clamping" approach).
//
// The fix is to make the fine layers' PARAMETERS depend on a slowly-varying field M(p).  Then,
// conditional on M, the noise is still stationary, but the material's LOCAL MEAN becomes
// E[c](p) = f(M(p)) — and a local mean is exactly what survives pixel averaging.  So zoom-out
// preserves f(M(p)) instead of collapsing.  Note this is not a hack bolted on top of AA: because
// M is ~constant across a pixel, f(M(p)) IS the correct answer to the AA integral for a
// non-stationary field.  We get structure at coarse zoom AND are more correct than before.
//
// THE CLOSE-ZOOM SAFETY PROPERTY (why this does not make close zoom "crazy"):
// The modulator's FINEST octave is deliberately kept far coarser than the material's own
// features (default finest cell ~50 m vs dirt's ~9 cm pebbles).  At close zoom M is therefore
// essentially CONSTANT across the whole screen — it just selects WHICH FLAVOUR of the material
// you are standing on (slightly gravelly vs. smooth soil), with no new high-frequency content.
// All the visible variation appears at wide zoom, which is precisely where it was missing.
//
// AA / DERIVATIVE SAFETY: the finest octave is additionally floored at MACRO_MOD_MIN_PX pixels
// (crossfaded, not hard-cut, so zooming does not pop).  This matters for two reasons:
//   1. A modulator with sub-pixel structure would itself alias — reintroducing the shimmer this
//      whole line of work is trying to remove.
//   2. Modulating a param adds a d(height)/dM * grad(M) term that fbmValD does NOT capture.  While
//      M is coarse that term is negligible; without the floor, a mis-authored config would
//      silently corrupt the analytic normals.
//
// ZOOM-STABLE GAIN: the octave sum is normalised by the FADE-WEIGHTED amplitude sum
// (Sum of amp*fade), not the all-octave sum.  Dividing by the all-octave sum makes the
// modulator's amplitude collapse at wide zoom (most octaves faded out, but still in the
// divisor) — the exact bug that was found and fixed in ambientDetailNormal.  With the
// fade-weighted divisor, `amount` is a pure gain knob with the same meaning at every zoom.
//
// NOTE (repo hazard): no backticks and no GLSL-ES-300 reserved identifiers (flat/smooth/...)
// inside the template literal.
export const MACRO_MOD_GLSL = `
// The per-recipe macro modulator, in roughly [-1, 1] and centred on 0.  Set ONCE per fragment
// per recipe at the top of eval_N (hoisted BEFORE the dChain call), so every layer body,
// derivative body and variance body in that recipe sees the SAME value — one noise evaluation
// per recipe per fragment, not one per layer.
float g_macroMod = 0.0;

// Minimum modulator cell size, in PIXELS.  The finest octave crossfades out below this so the
// modulator can never contribute sub-pixel structure (see the header: AA + derivative safety).
const float MACRO_MOD_MIN_PX = 16.0;

// Fractal value-noise modulator over p (world metres).  wavelength = the COARSEST octave's cell
// size in metres (intended to be map-scale, e.g. thousands of metres); the octave chain divides
// by lacunarity per step, so 'octaves' controls how fine the modulator is allowed to get.
// 'contrast' is an empirical output gain: a weighted average of independent octaves has a much
// smaller spread than any single octave, so without it the raw output would sit near 0 and the
// per-arg 'amount' values would all need to be implausibly large.  With it, m spans most of
// [-1, 1] and 'amount' reads naturally as "the swing this param gets".
// 'seedOff' decorrelates one recipe's modulator from another's (lattice offset, not a hash seed
// — valueNoise takes no seed argument; the offset is applied in each octave's own cell units).
float macroModVal(vec2 p, float wavelength, int octaves, float lacunarity, float persistence, float contrast, float seedOff) {
  float px = matPixelSize();
  float minCell = max(MACRO_MOD_MIN_PX * px, 1e-4);
  float cell = max(wavelength, 1e-4);
  float amp = 1.0;
  float total = 0.0;
  float norm = 0.0;
  // CONSTANT loop bound (D7: a dynamic bound is not portable-unrollable in GLSL ES 300) with a
  // uniform break at the configured octave count — the Fbm.glsl precedent.
  for (int o = 0; o < 8; o++) {
    if (o >= octaves) break;
    // Spec-clean ascending edges (edge0 < edge1) — the inverted form is UNDEFINED in GLSL ES 300
    // and has bitten this codebase repeatedly.  fade = 1 for cells well above the pixel floor,
    // 0 below it, crossfaded between so a zoom change never pops an octave in or out.
    float fade = smoothstep(minCell, 2.0 * minCell, cell);
    if (fade > 0.0) {
      vec2 off = vec2(seedOff + float(o) * 17.13, seedOff * 1.7 - float(o) * 29.71);
      float v = valueNoise(p / cell + off) * 2.0 - 1.0;
      total += amp * fade * v;
      norm += amp * fade;
    }
    amp *= persistence;
    cell /= max(lacunarity, 1.0001);
  }
  float m = norm > 1e-6 ? total / norm : 0.0;
  return clamp(m * contrast, -1.0, 1.0);
}
`;
